import { Injectable } from '@angular/core';
import { CanvasInputMode, CanvasInputModeService } from './canvas-input-mode.service';
import { CanvasService } from './canvas.service';
import { SelectedElementsService } from './selected-elements.service';
import { PositionAxis } from './element-position-handle-helper.service';

type MouseFuction = (event: MouseEvent) => void;
export type Point = [number, number];

export interface PolygonOptions {
    points: Point[];
    borderRadius?: number;
    closePath?: boolean;
}

@Injectable({ providedIn: 'root' })
export class TriangleDrawHelperService {
    newPolygonElement;

    private handleMouseDownFuction: MouseFuction;
    private handleMouseMoveFuction: MouseFuction;
    private handleMouseUpFuction: MouseFuction;

    readonly DIMENSION_W = 'W';
    readonly DIMENSION_H = 'H';

    readonly POSITION_X = PositionAxis.positionX;
    readonly POSITION_Y = PositionAxis.positionY;

    constructor(
        private inputMode: CanvasInputModeService,
        private canvasService: CanvasService,
        private selectionService: SelectedElementsService
    ) {
        this.init = this.init.bind(this);
    }

    init() {
        this.bindMouseEvents();
    }

    destroy() {
        this.unBindMouseEvents();
    }

    bindMouseEvents() {
        this.handleMouseDownFuction = this.handleMouseDown.bind(this);
        this.handleMouseMoveFuction = this.handleMouseMove.bind(this);
        this.handleMouseUpFuction = this.handleMouseUp.bind(this);

        document.addEventListener('mousedown', this.handleMouseDownFuction);
        document.addEventListener('mousemove', this.handleMouseMoveFuction);
        document.addEventListener('mouseup', this.handleMouseUpFuction);
    }

    unBindMouseEvents() {
        document.removeEventListener('mousedown', this.handleMouseDownFuction);
        document.removeEventListener('mousemove', this.handleMouseMoveFuction);
        document.removeEventListener('mouseup', this.handleMouseUpFuction);
    }

    createPathDAttribute(points, borderRadius) {
        return this.generateRoundedPath({
            points,
            borderRadius,
        });
    }

    private handleMouseDown(event) {
        if (this.inputMode.getMode() === CanvasInputMode.TRIANGLE) {
            const canvas = this.canvasService.getCanvas();
            const id = canvas.getNextId();
            const curShape = canvas.curShape;

            const zoom = canvas.getZoom();
            const pt = this.canvasService.transformPoint(event.clientX, event.clientY, canvas.getrootSctm());

            const mouseX = pt.x * zoom;
            const mouseY = pt.y * zoom;
            let x = mouseX / zoom;
            let y = mouseY / zoom;

            canvas.setStarted(true);
            canvas.setStartX(x);
            canvas.setStartY(y);

            const initTop = [x, y];
            const initBottomLeft = [x, y];
            const initBottomRight = [x, y];

            const dCreate = this.createPathDAttribute([initTop, initBottomLeft, initBottomRight], 0);

            this.newPolygonElement = canvas.addSVGElementsFromJson({
                element: 'path',
                curStyles: true,
                attr: {
                    d: dCreate,
                    id: id,
                    opacity: curShape.opacity,
                    'border-radius': 0,
                    type: CanvasInputMode.TRIANGLE,
                },
            });

            canvas.addCommandToHistory(
                new canvas.history.InsertElementCommand(this.newPolygonElement, 'insert triangle path')
            );
        }
    }

    private handleMouseUp() {
        if (this.inputMode.getMode() === CanvasInputMode.TRIANGLE) {
            if (this.newPolygonElement) {
                this.selectionService.setSelectedElements([this.newPolygonElement]);
            }
            this.clear();
            this.inputMode.setMode(CanvasInputMode.SELECT);
        }
    }

    private handleMouseMove(event) {
        if (this.inputMode.getMode() === CanvasInputMode.TRIANGLE) {
            const canvas = this.canvasService.getCanvas();
            const zoom = canvas.getZoom();
            const sctm = canvas.getrootSctm();

            if (!sctm) return;

            const pt = this.canvasService.transformPoint(event.clientX, event.clientY, canvas.getrootSctm());

            const mouseX = pt.x * zoom;
            const mouseY = pt.y * zoom;
            let x = mouseX / zoom;
            let y = mouseY / zoom;

            const startX = canvas.getStartX();
            const startY = canvas.getStartY();

            const moveTop = [startX, startY];
            const moveBottomRight = [x, y];
            const moveBottomLeft = [startX - (x - startX), y];

            const dMove = this.createPathDAttribute([moveTop, moveBottomRight, moveBottomLeft], 0);

            this.newPolygonElement?.setAttribute('d', dMove);
        }
    }

    clear() {
        this.newPolygonElement = null;
        return;
    }

    changeDimensions(element, value, options, constrained: boolean = false) {
        const bbox = element.getBBox();
        const x = bbox.x;
        const y = bbox.y;

        let newWidth: number;
        let newHeight: number;

        if (options === this.DIMENSION_W) {
            newWidth = Number(value);
            newHeight = constrained ? bbox.height * (newWidth / bbox.width) : bbox.height;
        } else {
            newHeight = Number(value);
            newWidth = constrained ? bbox.width * (newHeight / bbox.height) : bbox.width;
        }

        const top = [x + newWidth / 2, y];
        const bottomLeft = [x, y + newHeight];
        const bottomRight = [x + newWidth, y + newHeight];

        const borderRadius = element.getAttribute('border-radius');
        const d = this.createPathDAttribute([top, bottomLeft, bottomRight], borderRadius);

        element.setAttribute('d', d);
    }

    rebuildPath(element: SVGPathElement, newX: number, newY: number): void {
        const bbox = element.getBBox();
        const width = bbox.width;
        const height = bbox.height;
        const rx = width / 2;

        const top = [newX + rx, newY];
        const bottomLeft = [newX, newY + height];
        const bottomRight = [newX + width, newY + height];

        const borderRadiusAttr = element.getAttribute('border-radius');
        const borderRadius = borderRadiusAttr != null ? Number(borderRadiusAttr) : 0;

        const d = this.createPathDAttribute([top, bottomLeft, bottomRight], borderRadius);
        element.setAttribute('d', d);
    }

    applyRotation(element: SVGPathElement, newX: number, newY: number, axis: 'X' | 'Y'): void {
        const transform = element.getAttribute('transform');
        if (!transform || !transform.includes('rotate')) return;

        const match = transform.match(/rotate\(([-\d.]+)[ ,]+([-\d.]+)[ ,]+([-\d.]+)\)/);
        if (!match) return;

        const angle = parseFloat(match[1]);
        const oldCx = parseFloat(match[2]);
        const oldCy = parseFloat(match[3]);

        const bbox = element.getBBox();
        const cx = axis === this.POSITION_X ? newX + bbox.width / 2 : oldCx;
        const cy = axis === this.POSITION_Y ? newY + bbox.height / 2 : oldCy;

        element.setAttribute('transform', `rotate(${angle} ${cx} ${cy})`);
    }

    setAbsolutePosition(element: SVGPathElement, value: string, axis: 'X' | 'Y'): void {
        const bbox = element.getBBox();
        const baseX = bbox.x;
        const baseY = bbox.y;

        const newX = axis === this.POSITION_X ? Number(value) : baseX;
        const newY = axis === this.POSITION_Y ? Number(value) : baseY;

        this.rebuildPath(element, newX, newY);
        this.applyRotation(element, newX, newY, axis);
    }

    setRelativePosition(element: SVGPathElement, delta: number, axis: 'X' | 'Y'): void {
        const bbox = element.getBBox();
        const baseX = bbox.x;
        const baseY = bbox.y;

        const newX = axis === this.POSITION_X ? baseX + delta : baseX;
        const newY = axis === this.POSITION_Y ? baseY + delta : baseY;

        this.rebuildPath(element, newX, newY);
        this.applyRotation(element, newX, newY, axis);
    }

    changeRadius(element, value) {
        let top: [number, number];
        let bottomLeft: [number, number];
        let bottomRight: [number, number];

        const storedVertices = element.getAttribute('data-vertices');
        if (storedVertices) {
            const [t, bl, br] = JSON.parse(storedVertices);
            top = t;
            bottomLeft = bl;
            bottomRight = br;
        } else {
            const bbox = element.getBBox();
            top = [bbox.x + bbox.width / 2, bbox.y];
            bottomLeft = [bbox.x, bbox.y + bbox.height];
            bottomRight = [bbox.x + bbox.width, bbox.y + bbox.height];
            element.setAttribute('data-vertices', JSON.stringify([top, bottomLeft, bottomRight]));
        }

        const d = this.createPathDAttribute([top, bottomLeft, bottomRight], value);
        element.setAttribute('d', d);
        element.setAttribute('border-radius', value);
    }

    generateRoundedPath({ points, borderRadius = 0, closePath = true }: PolygonOptions): string {
        if (!points || points.length < 2) return '';

        const dParts: string[] = [];

        const getDistance = (a: Point, b: Point): number => Math.hypot(a[0] - b[0], a[1] - b[1]);

        const getAngle = (a: Point, b: Point): number => Math.atan2(b[1] - a[1], b[0] - a[0]);

        const pointCount = points.length;

        for (let i = 0; i < pointCount; i++) {
            const prev = points[(i - 1 + pointCount) % pointCount];
            const curr = points[i];
            const next = points[(i + 1) % pointCount];

            const prevAngle = getAngle(curr, prev);
            const nextAngle = getAngle(curr, next);

            const len = Math.min(getDistance(curr, prev), getDistance(curr, next));
            const r = Math.min(borderRadius, len / 2);

            const offsetPrev: Point = [curr[0] + Math.cos(prevAngle) * r, curr[1] + Math.sin(prevAngle) * r];
            const offsetNext: Point = [curr[0] + Math.cos(nextAngle) * r, curr[1] + Math.sin(nextAngle) * r];

            if (i === 0) {
                dParts.push(`M ${offsetPrev[0]} ${offsetPrev[1]}`);
            } else {
                dParts.push(`L ${offsetPrev[0]} ${offsetPrev[1]}`);
            }

            dParts.push(`Q ${curr[0]} ${curr[1]} ${offsetNext[0]} ${offsetNext[1]}`);
        }

        if (closePath) {
            dParts.push('Z');
        }

        return dParts.join(' ');
    }

    updateTrianglePathAndRotation(element: SVGPathElement, x: number, y: number, width: number, height: number): void {
        const top = [x + width / 2, y];
        const bottomLeft = [x, y + height];
        const bottomRight = [x + width, y + height];

        const borderRadiusAttr = element.getAttribute('border-radius');
        const borderRadius = borderRadiusAttr != null ? Number(borderRadiusAttr) : 0;

        const d = this.createPathDAttribute([top, bottomLeft, bottomRight], borderRadius);
        element.setAttribute('d', d);

        const transform = element.getAttribute('transform');
        if (!transform || !transform.includes('rotate')) return;

        const match = transform.match(/rotate\(([-\d.]+)[ ,]+([-\d.]+)[ ,]+([-\d.]+)\)/);
        if (!match) return;

        const angle = parseFloat(match[1]);
        const cx = x + width / 2;
        const cy = y + height / 2;

        element.setAttribute('transform', `rotate(${angle} ${cx} ${cy})`);
    }
}
